import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Affinity, SustainableFinanceDisclosureRegulationClobDto } from 'models/dtoTypes';
import { sortBy } from 'lodash';
import Stack from '@mui/material/Stack';
import SubSection from './_subSection';
import SdgScoreClob, { ISdgData } from './_sdgScoreClob';
import { taxonomySvg } from 'kosmos/images';
import EsgLegand from './_esgLegand';
import Empty from './_empty';
import { useTranslation } from 'react-i18next';

const PREFIX = 'SustainableFinanceDisclosureRegulationReport';

const classes = {
  item: `${PREFIX}-item`,
};

const Root = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  [`& .${classes.item}`]: {
    width: '100%',
    padding: 0,
  },
}));

interface IProps {
  name?: string;
  sustainableFinanceDisclosureRegulationReport: SustainableFinanceDisclosureRegulationClobDto[];
}

export default function SustainableFinanceDisclosureRegulationReport(props: IProps): JSX.Element {
  const { t } = useTranslation('default');
  const { sustainableFinanceDisclosureRegulationReport } = props;

  const sortedItems = useMemo(() => {
    return sortBy(sustainableFinanceDisclosureRegulationReport, 'number');
  }, [sustainableFinanceDisclosureRegulationReport]);

  const renderContent = () => {
    if (sortedItems.length === 0) {
      return <Empty title={t('there-is-no-data-available')} />;
    }

    return (
      <Root>
        <Stack direction="column" spacing={2}>
          {sortedItems.map((item, index) => (
            <div key={index} className={classes.item}>
              <SdgScoreClob
                weight="regular"
                key={index}
                withBg={true}
                content={item.affinity === Affinity.Unscored ? 'SG' : ''}
                title={item.title || ''}
                affinity={item.affinity}
              />
            </div>
          ))}
        </Stack>
      </Root>
    );
  };

  const renderLegands = () => {
    const legands: ISdgData[] = [
      {
        affinity: 'negative',
        title: t('negative-activity-found-towards-corresponding-sfdr'),
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'warning',
        title: t(
          'signs-of-both-positive-and-negative-activity-found-towards-corresponding-sfdr-but-mostly-negative',
        ),
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'positive',
        title: t('positive-activity-found-towards-corresponding-sfdr'),
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'neutral',
        title: t(
          'signs-of-balanced-positive-and-negative-activity-found-towards-corresponding-sfdr',
        ),
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'unscored',
        title: t(
          'not-applicable-or-insufficient-data—please-complete-disclosure-to-provide-necessary-details',
        ),
        withBg: true,
        content: 'SG',
        size: 'small',
      },
    ];

    return (
      <EsgLegand
        legands={legands}
        title={t(
          'activity-check-against-sustainable-finance-disclosure-regulation-sfdr-principle-adverse-impact-pai-score-mapped-using-positive-and-negative-sdgs',
        )}
        learnMoreTabId="sfdr"
      />
    );
  };

  return (
    <SubSection
      title={t('sustainable-finance-disclosure-regulation')}
      isBold
      icon={<img src={taxonomySvg} />}
    >
      <>
        {renderLegands()}
        {renderContent()}
      </>
    </SubSection>
  );
}
